import {
    Alert,
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import { useAuth } from 'react-oidc-context';
import DownloadJobRow from '../../components/Admin/DownloadJobRow';
import { useDeleteDownloadJobMutation, useGetAllDownloadJobsQuery } from '../../features/Admin/adminDownloadJobsApiSlice';

const AdminDownloadJobsPage: React.FC = () => {
    const auth = useAuth();
    const [deleteDownloadJob, { isLoading: isDeleting }] = useDeleteDownloadJobMutation();
    const { data: downloadJobsRaw = [], isLoading, error } = useGetAllDownloadJobsQuery(auth.user?.access_token ?? '', {
        pollingInterval: 30000,
    });

    // Sort jobs by createdTime descending
    const downloadJobs = [...downloadJobsRaw].sort((a, b) => {
        if (!a.createdTime && !b.createdTime) return 0;
        if (!a.createdTime) return 1;
        if (!b.createdTime) return -1;
        return new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime();
    });

    const getStatusColor = (state: string) => {
        switch (state) {
            case 'completed':
                return 'success';
            case 'failed':
                return 'error';
            case 'pending':
                return 'warning';
            case 'in-progress-making-zip':
            case 'in-progress-storing-zip':
                return 'info';
            default:
                return 'default';
        }
    };

    const getStatusText = (state: string) => {
        switch (state) {
            case 'pending':
                return 'Väntar';
            case 'in-progress-making-zip':
                return 'Skapar zip';
            case 'in-progress-storing-zip':
                return 'Sparar zip';
            case 'completed':
                return 'Klar';
            case 'failed':
                return 'Misslyckades';
            default:
                return state;
        }
    };

    const handleDelete = async (jobId: string) => {
        if (!window.confirm('Är du säker på att du vill ta bort detta nedladdningsjobb?')) return;
        try {
            await deleteDownloadJob({ jobId, accessToken: auth.user?.access_token ?? '' }).unwrap();
        } catch (e) {
            // Error handled by error state
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        try {
            return format(new Date(dateString), 'yyyy-MM-dd HH:mm:ss');
        } catch {
            return dateString;
        }
    };

    const formatTtlDate = (ttlSeconds?: number) => {
        if (!ttlSeconds) return '-';
        try {
            const date = new Date(ttlSeconds * 1000);
            return format(date, 'yyyy-MM-dd HH:mm:ss');
        } catch {
            return '-';
        }
    };

    const getProgressPercentage = (job: any) => {
        if (job.imagesToDownloadCount === 0) return 0;
        return Math.round((job.imagesProcessedCount / job.imagesToDownloadCount) * 100);
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ padding: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Nedladdningsjobb
                </Typography>
                <Alert severity="error">
                    Kunde inte ladda nedladdningsjobb. Försök igen senare.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom>
                Nedladdningsjobb
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Jobb-ID</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Antal bilder</TableCell>
                            <TableCell>Framsteg</TableCell>
                            <TableCell>Skapad</TableCell>
                            <TableCell>Senast ändrad</TableCell>
                            <TableCell>Klar</TableCell>
                            <TableCell>Förfaller</TableCell>
                            <TableCell>Felmeddelande</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {downloadJobs.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        Inga nedladdningsjobb hittades
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            downloadJobs.map((job) => (
                                <DownloadJobRow
                                    key={job.id}
                                    job={job}
                                    onDelete={handleDelete}
                                    isDeleting={isDeleting}
                                    getStatusText={getStatusText}
                                    getStatusColor={getStatusColor}
                                    formatDate={formatDate}
                                    formatTtlDate={formatTtlDate}
                                    getProgressPercentage={getProgressPercentage}
                                />
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default AdminDownloadJobsPage;
